import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Load the Dexterr Private Wealth logo as a data URI for embedding into PDFs.
 */
const loadLogoImage = async () => {
  try {
    const response = await fetch('/assets/images/dexterr-logo-1766247992978.png');
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to load logo image for PDF:', error);
    return null;
  }
};

/**
 * Utility functions for generating personalized PDF exports
 */

export const formatIndianCurrency = (amount) => {
  return '₹' + amount?.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date?.toLocaleDateString('en-IN', { 
    day: '2-digit', 
    month: 'short', 
    year: 'numeric' 
  });
};

export const getCurrentDate = () => {
  return new Date()?.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};

/**
 * Helper function to draw a simple table using basic jsPDF methods
 * @param {Object} pdf - jsPDF instance
 * @param {Array} headers - Array of header strings
 * @param {Array} rows - Array of row arrays
 * @param {number} startY - Starting Y position
 * @param {Object} options - Table styling options
 */
const drawSimpleTable = (pdf, headers, rows, startY, options = {}) => {
  const {
    headerColor = [59, 130, 246],
    headerTextColor = [255, 255, 255],
    rowColor = [255, 255, 255],
    alternateRowColor = [245, 245, 245],
    borderColor = [200, 200, 200],
    fontSize = 10,
    headerFontSize = 11,
    cellPadding = 5,
    startX = 20,
    tableWidth = 170
  } = options;

  const colWidth = tableWidth / headers?.length;
  let currentY = startY;

  // Draw header
  pdf?.setFillColor(...headerColor);
  pdf?.setDrawColor(...borderColor);
  pdf?.rect(startX, currentY, tableWidth, 8, 'FD');
  
  pdf?.setTextColor(...headerTextColor);
  pdf?.setFontSize(headerFontSize);
  pdf?.setFont('helvetica', 'bold');
  
  headers?.forEach((header, i) => {
    pdf?.text(header, startX + (i * colWidth) + cellPadding, currentY + 6);
  });
  
  currentY += 8;

  // Draw rows
  pdf?.setFontSize(fontSize);
  pdf?.setFont('helvetica', 'normal');
  
  rows?.forEach((row, rowIndex) => {
    // Alternate row colors
    const fillColor = rowIndex % 2 === 0 ? rowColor : alternateRowColor;
    pdf?.setFillColor(...fillColor);
    pdf?.rect(startX, currentY, tableWidth, 7, 'FD');
    
    pdf?.setTextColor(0, 0, 0);
    
    row?.forEach((cell, colIndex) => {
      const text = cell?.toString() || '';
      pdf?.text(text, startX + (colIndex * colWidth) + cellPadding, currentY + 5);
    });
    
    currentY += 7;
  });

  return currentY;
};

/**
 * Enhanced PDF generation using html2canvas for pixel-perfect web page capture
 * @param {HTMLElement} element - DOM element to capture
 * @param {string} fileName - Name for the PDF file
 * @param {Object} options - Additional options for PDF generation
 */
export const generateEnhancedPDF = async (element, fileName, options = {}) => {
  try {
    // Extract options
    const {
      orientation = 'portrait',
      format = 'a4',
      addMetadata = true,
      scale = 2,
      useCORS = true,
      userData = null,
      reportTitle = ''
    } = options;

    // Capture the element via html2canvas
    const canvas = await html2canvas(element, {
      scale,
      useCORS,
      logging: false,
      backgroundColor: '#ffffff',
      windowWidth: element?.scrollWidth,
      windowHeight: element?.scrollHeight
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF(orientation, 'mm', format);

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Reserve space (in mm) for header and footer
    const headerHeight = 45;
    const footerHeight = 30;
    const contentHeight = pageHeight - headerHeight - footerHeight;

    let heightLeft = (canvas.height * pageWidth) / canvas.width;
    let pageNumber = 0;

    // Preload logo
    const logoData = await loadLogoImage();

    // Prepare the legal-disclaimer text once
    const footerText =
      'Copyright © 2024 MUST DESRRUPT TECHNOLOGIES PRIVATE LIMITED All Rights Reserved.';
    const footerText2 =
      'Must Desrrupt Technologies Pvt. Ltd. is a SEBI/ AMFI Registered Mutual Fund Distributor with Registration Number: ARN 256384. ' +
      'We are also registered with SEBI as PMS Distributors with registration number: APRN04005. ' +
      'The company is registered with MCA with Registration No. – 161424 (RoC – Bangalore). CIN: U72900KA2022PTC161424.';
    const combinedFooter = `${footerText}\n${footerText2}`;

    while (heightLeft > 0) {
      if (pageNumber > 0) pdf.addPage();

      // Calculate cropping offset
      const offset = pageNumber * contentHeight;
      const position = headerHeight - offset;
      pdf.addImage(imgData, 'PNG', 0, position, pageWidth, (canvas.height * pageWidth) / canvas.width);

      // Mask original header and footer areas
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, pageWidth, headerHeight, 'F');
      pdf.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');

      // Header: logo and user details (first page only)
      // Start slightly lower to reduce top margin
      let headerY = 8;
      if (logoData) {
        const logoWidth = pageWidth * 0.6;
        const logoHeight = logoWidth * 0.25;
        const logoX = (pageWidth - logoWidth) / 2;
        pdf.addImage(logoData, 'PNG', logoX, headerY, logoWidth, logoHeight);
        headerY += logoHeight + 6; // reduced spacing after logo
      }

      if (pageNumber === 0 && userData) {
        const nameVal = userData.fullName || userData.name || '-';
        const emailVal = userData.email || '-';
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(`Name: ${nameVal}`, 20, headerY);
        pdf.text(`Email: ${emailVal}`, pageWidth - 20, headerY, { align: 'right' });
        headerY += 8;  // tighter spacing before content
      }

      // Footer: white background + disclaimer across full width
      // Clear the footer area so it’s not affected by the captured page
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, pageHeight - 20, pageWidth, 20, 'F');

      // Draw the legal text, letting jsPDF wrap it across (pageWidth - 10)
      pdf.setFontSize(6);
      pdf.setTextColor(128, 128, 128);
      pdf.text(combinedFooter, 5, pageHeight - 12, { maxWidth: pageWidth - 10 });

      heightLeft -= contentHeight;
      pageNumber++;
    }

    // Metadata
    if (addMetadata) {
      pdf.setProperties({
        title: fileName.replace(/\.pdf$/, ''),
        subject: 'Financial Planning Document',
        author: 'F.I.R.E by Dexterr Private Wealth',
        keywords: 'retirement, financial planning, goals',
        creator: 'F.I.R.E by Dexterr Private Wealth Application'
      });
    }

    pdf.save(fileName);
    return { success: true, fileName };
  } catch (error) {
    console.error('Error generating enhanced PDF:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Generate PDF for Retirement Planning with enhanced visual design matching
 * @param {Object} userData - User personal data (name, age, etc.)
 * @param {Object} projectionData - Retirement calculation data
 * @param {number} retirementAge - Target retirement age
 * @param {boolean} useEnhanced - Whether to use html2canvas for pixel-perfect capture
 */
export const generateRetirementPDF = async (userData, projectionData, retirementAge, useEnhanced = false) => {
  // If enhanced mode is requested, delegate to enhanced PDF generator
  if (useEnhanced) {
    const retirementPage = document.querySelector('.retirement-planning-page');
    if (retirementPage) {
      return await generateEnhancedPDF(
        retirementPage,
        `Retirement_Plan_${userData?.name?.replace(/\s+/g, '_')}_${new Date()?.toISOString()?.split('T')[0]}.pdf`,
        {
          orientation: 'portrait',
          addMetadata: true,
          userData: userData,
          reportTitle: ''
        }
      );
    }
  }

  try {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf?.internal?.pageSize?.getWidth();
    const pageHeight = pdf?.internal?.pageSize?.getHeight();
    // === New header section ===
    // Draw the Dexterr Private Wealth logo centered at the top
    const logoData = await loadLogoImage();
    const logoWidth = pageWidth * 0.6;
    const logoHeight = logoWidth * 0.25;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;
    if (logoData) {
      pdf?.addImage(logoData, 'PNG', logoX, logoY, logoWidth, logoHeight);
    }
    // Starting Y position just below the logo
    let yPosition = logoY + logoHeight + 8;
    // Personalised user details line
    const name = userData?.fullName || userData?.name || '-';
    const email = userData?.email || '-';
    pdf?.setFontSize(12);
    pdf?.setFont('helvetica', 'bold');
    pdf?.setTextColor(0, 0, 0);
    // Print name and email only (omit date of birth)
    pdf?.text(`Name: ${name}`, 20, yPosition);
    pdf?.text(`Email: ${email}`, pageWidth - 20, yPosition, { align: 'right' });
    yPosition += 10;

    // Personal Information Section
    pdf?.setTextColor(0, 0, 0);
    pdf?.setFontSize(16);
    pdf?.setFont('helvetica', 'bold');
    pdf?.text('Personal Information', 20, yPosition);
    yPosition += 10;

    pdf?.setFontSize(11);
    pdf?.setFont('helvetica', 'normal');
    const personalInfo = [
      ['Full Name:', userData?.name || 'N/A'],
      ['Current Age:', `${userData?.currentAge || 'N/A'} years`],
      ['Target Retirement Age:', `${retirementAge || 'N/A'} years`],
      ['Years to Retirement:', `${retirementAge - userData?.currentAge || 'N/A'} years`],
      ['Current Monthly Income:', formatIndianCurrency(userData?.monthlyIncome || 0)],
    ];

    personalInfo?.forEach(([label, value]) => {
      pdf?.setFont('helvetica', 'bold');
      pdf?.text(label, 20, yPosition);
      pdf?.setFont('helvetica', 'normal');
      pdf?.text(value, 80, yPosition);
      yPosition += 7;
    });

    yPosition += 5;

    // Financial Parameters Section
    pdf?.setFontSize(16);
    pdf?.setFont('helvetica', 'bold');
    pdf?.text('Financial Parameters', 20, yPosition);
    yPosition += 10;

    pdf?.setFontSize(11);
    pdf?.setFont('helvetica', 'normal');
    const financialParams = [
      ['Pre-Retirement Inflation:', `${userData?.preRetirementInflation || 'N/A'}%`],
      ['Post-Retirement Inflation:', `${userData?.postRetirementInflation || 'N/A'}%`],
      ['Current Savings Rate:', `${projectionData?.savingsRate || 'N/A'}%`],
      ['Expected Returns:', `${projectionData?.expectedReturns || 'N/A'}%`],
    ];

    financialParams?.forEach(([label, value]) => {
      pdf?.setFont('helvetica', 'bold');
      pdf?.text(label, 20, yPosition);
      pdf?.setFont('helvetica', 'normal');
      pdf?.text(value, 80, yPosition);
      yPosition += 7;
    });

    yPosition += 5;

    // Retirement Projections Section
    if (projectionData) {
      pdf?.setFontSize(16);
      pdf?.setFont('helvetica', 'bold');
      pdf?.text('Retirement Projections', 20, yPosition);
      yPosition += 10;

      pdf?.setFontSize(11);
      pdf?.setFont('helvetica', 'normal');
      const projections = [
        ['Required Monthly Income at Retirement:', formatIndianCurrency(projectionData?.requiredMonthlyIncome || 0)],
        ['Total Corpus Required:', formatIndianCurrency(projectionData?.totalCorpusRequired || 0)],
        ['Monthly Savings Required:', formatIndianCurrency(projectionData?.monthlySavingsRequired || 0)],
        ['Annual Savings Required:', formatIndianCurrency((projectionData?.monthlySavingsRequired || 0) * 12)],
      ];

      projections?.forEach(([label, value]) => {
        pdf?.setFont('helvetica', 'bold');
        pdf?.text(label, 20, yPosition);
        pdf?.setFont('helvetica', 'normal');
        pdf?.text(value, 100, yPosition);
        yPosition += 7;
      });

      yPosition += 5;

      // Retirement Income Details
      if (projectionData?.retirementYears) {
        pdf?.setFontSize(14);
        pdf?.setFont('helvetica', 'bold');
        pdf?.text('Post-Retirement Income Planning', 20, yPosition);
        yPosition += 8;

        pdf?.setFontSize(11);
        pdf?.setFont('helvetica', 'normal');
        const retirementDetails = [
          ['Expected Retirement Duration:', `${projectionData?.retirementYears || 'N/A'} years`],
          ['Monthly Pension/Income:', formatIndianCurrency(projectionData?.monthlyPension || 0)],
          ['Corpus Withdrawal Rate:', `${projectionData?.withdrawalRate || 'N/A'}%`],
        ];

        retirementDetails?.forEach(([label, value]) => {
          pdf?.setFont('helvetica', 'bold');
          pdf?.text(label, 20, yPosition);
          pdf?.setFont('helvetica', 'normal');
          pdf?.text(value, 90, yPosition);
          yPosition += 7;
        });
      }
    }

    yPosition += 10;

    // Recommendations Section
    if (yPosition > pageHeight - 60) {
      pdf?.addPage();
      yPosition = 20;
    }

    pdf?.setFillColor(240, 240, 240);
    pdf?.rect(15, yPosition - 5, pageWidth - 30, 40, 'F');
    
    pdf?.setFontSize(14);
    pdf?.setFont('helvetica', 'bold');
    pdf?.setTextColor(59, 130, 246);
    pdf?.text('Key Recommendations', 20, yPosition + 2);
    
    pdf?.setFontSize(10);
    pdf?.setFont('helvetica', 'normal');
    pdf?.setTextColor(0, 0, 0);
    yPosition += 10;

    const recommendations = [
      '• Start investing early to benefit from compound returns',
      '• Diversify investments across equity, debt, and other asset classes',
      '• Review and adjust your plan annually based on life changes',
      '• Consider inflation-protected investments for long-term goals',
      '• Consult a certified financial planner for personalized advice',
    ];

    recommendations?.forEach(rec => {
      pdf?.text(rec, 20, yPosition);
      yPosition += 5;
    });
    // Footer with legal disclaimer and copyright
    const footerText =
      'Copyright © 2024 MUST DESRRUPT TECHNOLOGIES PRIVATE LIMITED All Rights Reserved.';
    const footerText2 =
      'Must Desrrupt Technologies Pvt. Ltd. is a SEBI/ AMFI Registered Mutual Fund Distributor with Registration Number: ARN 256384. ' +
      'We are also registered with SEBI as PMS Distributors with registration number: APRN04005. ' +
      'The company is registered with MCA with Registration No. – 161424 (RoC – Bangalore). CIN: U72900KA2022PTC161424.';
    const combinedFooter = `${footerText}\n${footerText2}`;

    // Clear the footer area
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, pageHeight - 20, pageWidth, 20, 'F');

    // Draw the text with wide wrapping
    pdf.setFontSize(6);
    pdf.setTextColor(128, 128, 128);
    pdf.text(combinedFooter, 5, pageHeight - 12, { maxWidth: pageWidth - 10 });

    // Save PDF
    const fileName = `Retirement_Plan_${userData?.name?.replace(/\s+/g, '_')}_${new Date()?.toISOString()?.split('T')?.[0]}.pdf`;
    pdf?.save(fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Error generating retirement PDF:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Generate PDF for Life Goals with enhanced visual design matching
 * @param {Object} userData - User personal data
 * @param {Array} goals - Array of life goals
 * @param {boolean} useEnhanced - Whether to use html2canvas for pixel-perfect capture
 */
export const generateLifeGoalsPDF = async (userData, goals, useEnhanced = false) => {
  // If enhanced mode is requested, delegate to enhanced PDF generator
  if (useEnhanced) {
    const lifeGoalsPage = document.querySelector('.life-goals-page');
    if (lifeGoalsPage) {
      return await generateEnhancedPDF(
        lifeGoalsPage,
        `Life_Goals_${userData?.name?.replace(/\s+/g, '_')}_${new Date()?.toISOString()?.split('T')[0]}.pdf`,
        {
          orientation: 'portrait',
          addMetadata: true,
          userData: userData,
          reportTitle: ''
        }
      );
    }
  }

  try {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf?.internal?.pageSize?.getWidth();
    const pageHeight = pdf?.internal?.pageSize?.getHeight();
    // === New header section ===
    // Draw logo centered
    const logoData = await loadLogoImage();
    const logoWidth = pageWidth * 0.6;
    const logoHeight = logoWidth * 0.25;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;
    if (logoData) {
      pdf?.addImage(logoData, 'PNG', logoX, logoY, logoWidth, logoHeight);
    }
    let yPosition = logoY + logoHeight + 8;
    // User details line
    const name = userData?.fullName || userData?.name || '-';
    const email = userData?.email || '-';
    pdf?.setFontSize(12);
    pdf?.setFont('helvetica', 'bold');
    pdf?.setTextColor(0, 0, 0);
    pdf?.text(`Name: ${name}`, 20, yPosition);
    pdf?.text(`Email: ${email}`, pageWidth - 20, yPosition, { align: 'right' });
    yPosition += 10;

    // Personal Information
    pdf?.setTextColor(0, 0, 0);
    pdf?.setFontSize(16);
    pdf?.setFont('helvetica', 'bold');
    pdf?.text('Personal Information', 20, yPosition);
    yPosition += 10;

    pdf?.setFontSize(11);
    pdf?.setFont('helvetica', 'normal');
    const personalInfo = [
      ['Full Name:', userData?.name || 'N/A'],
      ['Current Age:', `${userData?.currentAge || 'N/A'} years`],
      ['Total Goals:', `${goals?.length || 0}`],
    ];

    personalInfo?.forEach(([label, value]) => {
      pdf?.setFont('helvetica', 'bold');
      pdf?.text(label, 20, yPosition);
      pdf?.setFont('helvetica', 'normal');
      pdf?.text(value, 70, yPosition);
      yPosition += 7;
    });

    yPosition += 5;

    // Goals Summary
    pdf?.setFontSize(16);
    pdf?.setFont('helvetica', 'bold');
    pdf?.text('Goals Summary', 20, yPosition);
    yPosition += 10;

    const totalTargetAmount = goals?.reduce((sum, goal) => sum + (goal?.targetAmount || 0), 0);
    const totalCurrentSavings = goals?.reduce((sum, goal) => sum + (goal?.currentSavings || 0), 0);
    const totalMonthlySavings = goals?.reduce((sum, goal) => sum + (goal?.monthlySavingsRequired || 0), 0);

    pdf?.setFontSize(11);
    const summaryData = [
      ['Total Target Amount:', formatIndianCurrency(totalTargetAmount)],
      ['Total Current Savings:', formatIndianCurrency(totalCurrentSavings)],
      ['Total Monthly Savings Required:', formatIndianCurrency(totalMonthlySavings)],
      ['Total Annual Savings Required:', formatIndianCurrency(totalMonthlySavings * 12)],
    ];

    summaryData?.forEach(([label, value]) => {
      pdf?.setFont('helvetica', 'bold');
      pdf?.text(label, 20, yPosition);
      pdf?.setFont('helvetica', 'normal');
      pdf?.text(value, 100, yPosition);
      yPosition += 7;
    });

    yPosition += 10;

    // Individual Goals Details
    pdf?.setFontSize(16);
    pdf?.setFont('helvetica', 'bold');
    pdf?.text('Individual Goals', 20, yPosition);
    yPosition += 10;

    goals?.forEach((goal, index) => {
      // Check if we need a new page
      if (yPosition > pageHeight - 70) {
        pdf?.addPage();
        yPosition = 20;
      }

      // Goal header with background
      pdf?.setFillColor(245, 245, 245);
      pdf?.rect(15, yPosition - 5, pageWidth - 30, 8, 'F');
      
      pdf?.setFontSize(14);
      pdf?.setFont('helvetica', 'bold');
      pdf?.setTextColor(59, 130, 246);
      pdf?.text(`${index + 1}. ${goal?.name || 'Unnamed Goal'}`, 20, yPosition);
      yPosition += 10;

      pdf?.setFontSize(10);
      pdf?.setFont('helvetica', 'normal');
      pdf?.setTextColor(0, 0, 0);

      const goalType = goal?.type?.charAt(0)?.toUpperCase() + goal?.type?.slice(1) || 'Custom';
      const progressPercentage = ((goal?.currentSavings || 0) / (goal?.targetAmount || 1) * 100)?.toFixed(1);
      
      const goalDetails = [
        ['Goal Type:', goalType],
        ['Target Amount:', formatIndianCurrency(goal?.targetAmount || 0)],
        ['Current Savings:', formatIndianCurrency(goal?.currentSavings || 0)],
        ['Target Date:', formatDate(goal?.targetDate)],
        ['Monthly Savings Required:', formatIndianCurrency(goal?.monthlySavingsRequired || 0)],
        ['Expected Returns:', `${goal?.expectedReturns || 'N/A'}%`],
        ['Inflation Rate:', `${goal?.inflationRate || 'N/A'}%`],
        ['Progress:', `${progressPercentage}%`],
      ];

      goalDetails?.forEach(([label, value]) => {
        pdf?.setFont('helvetica', 'bold');
        pdf?.text(label, 25, yPosition);
        pdf?.setFont('helvetica', 'normal');
        pdf?.text(value, 85, yPosition);
        yPosition += 6;
      });

      yPosition += 8;
    });

    // Recommendations
    if (yPosition > pageHeight - 50) {
      pdf?.addPage();
      yPosition = 20;
    }

    pdf?.setFillColor(240, 240, 240);
    pdf?.rect(15, yPosition - 5, pageWidth - 30, 35, 'F');
    
    pdf?.setFontSize(14);
    pdf?.setFont('helvetica', 'bold');
    pdf?.setTextColor(59, 130, 246);
    pdf?.text('Key Recommendations', 20, yPosition + 2);
    
    pdf?.setFontSize(10);
    pdf?.setFont('helvetica', 'normal');
    pdf?.setTextColor(0, 0, 0);
    yPosition += 10;

    const recommendations = [
      '• Prioritize goals based on urgency and importance',
      '• Set up automatic monthly transfers to goal-specific accounts',
      '• Review progress quarterly and adjust savings if needed',
      '• Consider systematic investment plans (SIP) for long-term goals',
    ];

    recommendations?.forEach(rec => {
      pdf?.text(rec, 20, yPosition);
      yPosition += 5;
    });

    // Footer with legal disclaimer and copyright
    const footerText =
      'Copyright © 2024 MUST DESRRUPT TECHNOLOGIES PRIVATE LIMITED All Rights Reserved.';
    const footerText2 =
      'Must Desrrupt Technologies Pvt. Ltd. is a SEBI/ AMFI Registered Mutual Fund Distributor with Registration Number: ARN 256384. ' +
      'We are also registered with SEBI as PMS Distributors with registration number: APRN04005. ' +
      'The company is registered with MCA with Registration No. – 161424 (RoC – Bangalore). CIN: U72900KA2022PTC161424.';
    const combinedFooter = `${footerText}\n${footerText2}`;
    const footerLines = pdf.splitTextToSize(combinedFooter, pageWidth - 10);
    pdf.setFontSize(6);
    pdf.setTextColor(128, 128, 128);
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, pageHeight - 20, pageWidth, 20, 'F');
    pdf.text(footerLines, 5, pageHeight - 10);

    // Save PDF
    const fileName = `Life_Goals_${userData?.name?.replace(/\s+/g, '_')}_${new Date()?.toISOString()?.split('T')?.[0]}.pdf`;
    pdf?.save(fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Error generating life goals PDF:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Generate comprehensive PDF for Financial Dashboard with enhanced visual design matching
 * @param {Object} userData - User personal data
 * @param {Object} dashboardData - Complete dashboard data including summary, goals, charts
 * @param {boolean} useEnhanced - Whether to use html2canvas for pixel-perfect capture
 */
export const generateDashboardPDF = async (userData, dashboardData, useEnhanced = false) => {
  // If enhanced mode is requested, delegate to enhanced PDF generator
  if (useEnhanced) {
    const dashboardPage = document.querySelector('.financial-dashboard-page');
    if (dashboardPage) {
      return await generateEnhancedPDF(
        dashboardPage,
        `Financial_Dashboard_${userData?.name?.replace(/\s+/g, '_')}_${new Date()?.toISOString()?.split('T')[0]}.pdf`,
        {
          orientation: 'portrait',
          addMetadata: true,
          userData: userData,
          reportTitle: ''
        }
      );
    }
  }

  try {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf?.internal?.pageSize?.getWidth();
    const pageHeight = pdf?.internal?.pageSize?.getHeight();
    // === New header section ===
    // Draw logo centered at the top
    const logoData = await loadLogoImage();
    const logoWidth = pageWidth * 0.6;
    const logoHeight = logoWidth * 0.25;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;
    if (logoData) {
      pdf?.addImage(logoData, 'PNG', logoX, logoY, logoWidth, logoHeight);
    }
    let yPosition = logoY + logoHeight + 8;
    // User details line
    const name = userData?.fullName || userData?.name || '-';
    const email = userData?.email || '-';
    pdf?.setFontSize(12);
    pdf?.setFont('helvetica', 'bold');
    pdf?.setTextColor(0, 0, 0);
    pdf?.text(`Name: ${name}`, 20, yPosition);
    pdf?.text(`Email: ${email}`, pageWidth - 20, yPosition, { align: 'right' });
    yPosition += 10;

    // Dashboard Summary Section - mimics the 4 summary cards
    pdf?.setTextColor(0, 0, 0);
    pdf?.setFontSize(18);
    pdf?.setFont('helvetica', 'bold');
    pdf?.text('Financial Overview', 20, yPosition);
    yPosition += 12;

    // Create summary cards layout
    const summaryCards = [
      {
        title: 'Total Financial Goals',
        value: dashboardData?.summary?.totalGoals?.toString() || '0',
        subtitle: 'Active goals being tracked',
        color: [220, 220, 220]
      },
      {
        title: 'Retirement Readiness',
        value: `${dashboardData?.summary?.retirementReadiness || 0}%`,
        subtitle: 'On track for retirement',
        color: [34, 197, 94]
      },
      {
        title: 'Monthly Savings Required',
        value: dashboardData?.summary?.monthlySavingsRequired || '₹0',
        subtitle: 'To achieve all goals',
        color: [59, 130, 246]
      },
      {
        title: 'Projected Wealth',
        value: dashboardData?.summary?.projectedWealth || '₹0',
        subtitle: 'At retirement age',
        color: [34, 197, 94]
      }
    ];

    // Draw summary cards in 2x2 grid
    const cardWidth = (pageWidth - 50) / 2;
    const cardHeight = 35;
    let cardX = 20;
    let cardY = yPosition;

    summaryCards?.forEach((card, index) => {
      // Draw card background
      pdf?.setFillColor(...card?.color);
      pdf?.roundedRect(cardX, cardY, cardWidth, cardHeight, 3, 3, 'F');
      
      // Card title
      pdf?.setTextColor(255, 255, 255);
      pdf?.setFontSize(10);
      pdf?.setFont('helvetica', 'bold');
      pdf?.text(card?.title, cardX + 5, cardY + 8);
      
      // Card value
      pdf?.setFontSize(16);
      pdf?.setFont('helvetica', 'bold');
      pdf?.text(card?.value, cardX + 5, cardY + 18);
      
      // Card subtitle
      pdf?.setFontSize(8);
      pdf?.setFont('helvetica', 'normal');
      pdf?.text(card?.subtitle, cardX + 5, cardY + 25);

      // Move to next position
      if (index % 2 === 0) {
        cardX = pageWidth - cardWidth - 20;
      } else {
        cardX = 20;
        cardY += cardHeight + 5;
      }
    });

    yPosition = cardY + 10;

    // Active Alerts Section
    if (dashboardData?.alerts?.length > 0) {
      pdf?.setTextColor(0, 0, 0);
      pdf?.setFontSize(16);
      pdf?.setFont('helvetica', 'bold');
      pdf?.text('Active Alerts & Notifications', 20, yPosition);
      yPosition += 10;

      dashboardData?.alerts?.forEach((alert, index) => {
        if (yPosition > pageHeight - 40) {
          pdf?.addPage();
          yPosition = 20;
        }

        // Alert color based on type
        const alertColors = {
          'warning': [255, 193, 7],
          'success': [34, 197, 94],
          'info': [59, 130, 246]
        };
        
        const color = alertColors?.[alert?.type] || [128, 128, 128];
        
        pdf?.setFillColor(...color);
        pdf?.rect(20, yPosition - 3, 3, 8, 'F');
        
        pdf?.setFontSize(11);
        pdf?.setFont('helvetica', 'bold');
        pdf?.text(alert?.title || 'Alert', 26, yPosition);
        
        pdf?.setFontSize(9);
        pdf?.setFont('helvetica', 'normal');
        const messageLines = pdf?.splitTextToSize(alert?.message || '', pageWidth - 50);
        pdf?.text(messageLines, 26, yPosition + 5);
        
        yPosition += 10 + (messageLines?.length * 4);
      });

      yPosition += 5;
    }

    // Savings Allocation Section - Using custom table drawer
    if (yPosition > pageHeight - 80) {
      pdf?.addPage();
      yPosition = 20;
    }

    pdf?.setFontSize(16);
    pdf?.setFont('helvetica', 'bold');
    pdf?.text('Savings Allocation Breakdown', 20, yPosition);
    yPosition += 10;

    if (dashboardData?.savingsAllocation?.length > 0) {
      // Create table data
      const tableHeaders = ['Goal Category', 'Monthly Amount', 'Percentage'];
      const tableRows = dashboardData?.savingsAllocation?.map(item => [
        item?.name || 'N/A',
        formatIndianCurrency(item?.value || 0),
        `${item?.percentage || 0}%`
      ]);

      yPosition = drawSimpleTable(pdf, tableHeaders, tableRows, yPosition, {
        startX: 20,
        tableWidth: pageWidth - 40
      });

      yPosition += 15;
    }

    // Active Goals Overview Section
    if (yPosition > pageHeight - 60) {
      pdf?.addPage();
      yPosition = 20;
    }

    pdf?.setFontSize(16);
    pdf?.setFont('helvetica', 'bold');
    pdf?.text('Active Financial Goals', 20, yPosition);
    yPosition += 10;

    if (dashboardData?.goals?.length > 0) {
      dashboardData?.goals?.forEach((goal, index) => {
        if (yPosition > pageHeight - 50) {
          pdf?.addPage();
          yPosition = 20;
        }

        // Goal header with priority indicator
        const priorityColors = {
          'high': [239, 68, 68],
          'medium': [251, 146, 60],
          'low': [34, 197, 94]
        };
        
        const priorityColor = priorityColors?.[goal?.priority] || [128, 128, 128];
        
        pdf?.setFillColor(245, 245, 245);
        pdf?.rect(20, yPosition - 4, pageWidth - 40, 10, 'F');
        
        pdf?.setFillColor(...priorityColor);
        pdf?.circle(25, yPosition, 2, 'F');
        
        pdf?.setTextColor(0, 0, 0);
        pdf?.setFontSize(13);
        pdf?.setFont('helvetica', 'bold');
        pdf?.text(`${index + 1}. ${goal?.name || 'Unnamed Goal'}`, 30, yPosition);
        
        pdf?.setFontSize(9);
        pdf?.setFont('helvetica', 'normal');
        pdf?.setTextColor(100, 100, 100);
        pdf?.text(`${goal?.category || 'Custom'} | ${goal?.timeline || 'N/A'}`, 30, yPosition + 4);
        
        yPosition += 12;

        // Goal details
        pdf?.setTextColor(0, 0, 0);
        pdf?.setFontSize(10);
        const goalDetails = [
          ['Target Amount:', goal?.targetAmount || '₹0'],
          ['Current Savings:', goal?.currentSavings || '₹0'],
          ['Progress:', `${goal?.progress || 0}%`],
          ['Next Milestone:', goal?.nextMilestone || 'N/A']
        ];

        goalDetails?.forEach(([label, value]) => {
          pdf?.setFont('helvetica', 'bold');
          pdf?.text(label, 30, yPosition);
          pdf?.setFont('helvetica', 'normal');
          pdf?.text(value, 85, yPosition);
          yPosition += 5;
        });

        // Progress bar
        const barWidth = pageWidth - 60;
        const barHeight = 4;
        const progressWidth = (barWidth * (goal?.progress || 0)) / 100;
        
        pdf?.setDrawColor(220, 220, 220);
        pdf?.setFillColor(220, 220, 220);
        pdf?.rect(30, yPosition, barWidth, barHeight, 'F');
        
        pdf?.setFillColor(59, 130, 246);
        if (progressWidth > 0) {
          pdf?.rect(30, yPosition, progressWidth, barHeight, 'F');
        }

        yPosition += 12;
      });
    }

    // Retirement Projection Section - Using custom table drawer
    if (dashboardData?.retirementProjection?.length > 0) {
      if (yPosition > pageHeight - 80) {
        pdf?.addPage();
        yPosition = 20;
      }

      pdf?.setFontSize(16);
      pdf?.setFont('helvetica', 'bold');
      pdf?.text('Retirement Corpus Projection', 20, yPosition);
      yPosition += 10;

      const projectionHeaders = ['Year', 'Projected Corpus', 'Required Corpus'];
      const projectionRows = dashboardData?.retirementProjection?.map(item => [
        item?.year?.toString() || 'N/A',
        formatIndianCurrency(item?.projected || 0),
        formatIndianCurrency(item?.required || 0)
      ]);

      yPosition = drawSimpleTable(pdf, projectionHeaders, projectionRows, yPosition, {
        startX: 20,
        tableWidth: pageWidth - 40
      });

      yPosition += 15;
    }

    // Key Recommendations Section
    if (yPosition > pageHeight - 60) {
      pdf?.addPage();
      yPosition = 20;
    }

    pdf?.setFillColor(240, 240, 240);
    pdf?.rect(15, yPosition - 5, pageWidth - 30, 50, 'F');
    
    pdf?.setFontSize(14);
    pdf?.setFont('helvetica', 'bold');
    pdf?.setTextColor(59, 130, 246);
    pdf?.text('Financial Planning Recommendations', 20, yPosition + 2);
    
    pdf?.setFontSize(10);
    pdf?.setFont('helvetica', 'normal');
    pdf?.setTextColor(0, 0, 0);
    yPosition += 10;

    const recommendations = [
      '• Review your financial goals quarterly to ensure alignment with current life situation',
      '• Diversify investments across asset classes to optimize risk-reward balance',
      '• Maintain emergency fund covering 6 months of expenses before aggressive investing',
      '• Consider tax-efficient investment options to maximize post-tax returns',
      '• Increase SIP contributions annually in line with salary increments',
      '• Consult a certified financial planner for personalized investment advice'
    ];

    recommendations?.forEach(rec => {
      const lines = pdf?.splitTextToSize(rec, pageWidth - 45);
      pdf?.text(lines, 20, yPosition);
      yPosition += 5 + (lines?.length - 1) * 4;
    });

    // Footer with legal disclaimer and copyright
    const footerText =
      'Copyright © 2024 MUST DESRRUPT TECHNOLOGIES PRIVATE LIMITED All Rights Reserved.';
    const footerText2 =
      'Must Desrrupt Technologies Pvt. Ltd. is a SEBI/ AMFI Registered Mutual Fund Distributor with Registration Number: ARN 256384. ' +
      'We are also registered with SEBI as PMS Distributors with registration number: APRN04005. ' +
      'The company is registered with MCA with Registration No. – 161424 (RoC – Bangalore). CIN: U72900KA2022PTC161424.';
    const combinedFooter = `${footerText}\n${footerText2}`;
    const footerLines = pdf.splitTextToSize(combinedFooter, pageWidth - 10);
    pdf.setFontSize(6);
    pdf.setTextColor(128, 128, 128);
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, pageHeight - 20, pageWidth, 20, 'F');
    pdf.text(footerLines, 5, pageHeight - 10);

    // Save PDF
    const fileName = `Financial_Dashboard_${userData?.name?.replace(/\s+/g, '_')}_${new Date()?.toISOString()?.split('T')?.[0]}.pdf`;
    pdf?.save(fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Error generating dashboard PDF:', error);
    return { success: false, error: error?.message };
  }
};